/* eslint-disable */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
// material
import { TableBody, TableCell, Container } from '@mui/material';
import { Typography, TableContainer, TablePagination, IconButton } from '@mui/material';
import { Card, Table, Stack, TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip } from '@mui/material';
// components
import Page from '../../../components/Page';
import Scrollbar from '../../../components/Scrollbar';
import Iconify from '../../../components/Iconify';
import SearchNotFound from '../../../components/SearchNotFound';
import { ListHead, ListToolbar } from '../../../sections/@dashboard/table';
import Loader from '../../../components/Loader';
import { formatDate } from '../../../utils/commonFunc';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'userId', label: 'User ID', alignRight: false },
  { id: 'userName', label: 'User Name', alignRight: false },
  { id: 'userEmail', label: 'User Email', alignRight: false },
  { id: 'customerId', label: 'Customer ID', alignRight: false },
  { id: 'customerName', label: 'Customer Name', alignRight: false },
  { id: 'customerEmail', label: 'Customer Email', alignRight: false },
  { id: 'workStart', label: 'Work Start', alignRight: false },
  { id: 'description', label: 'Description', alignRight: false },
];

const preInsuranceDropOptions = [
  { id: 1, label: 'User ID', alignRight: false },
  { id: 2, label: 'User Name', alignRight: false },
  { id: 3, label: 'User Email', alignRight: false },
  { id: 4, label: 'Customer ID', alignRight: false },
  { id: 5, label: 'Customer Name', alignRight: false },
  { id: 6, label: 'Customer Email', alignRight: false },
];

// ----------------------------------------------------------------------

export default function PreInsurance() {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [openDescDialog, setOpenDescDialog] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDropValue, setSearchDropValue] = useState('');

  const navigate = useNavigate();

  // Helper function to truncate description
  const truncateDescription = (text) => {
    if (!text) return 'N/A';
    const words = text.trim().split(/\s+/);
    if (words.length > 10) {
      return words.slice(0, 10).join(' ') + '...';
    }
    return text;
  };

  const handleOpenDescDialog = (description) => {
    setSelectedDescription(description || 'N/A');
    setOpenDescDialog(true);
  };

  const handleCloseDescDialog = () => {
    setOpenDescDialog(false);
    setSelectedDescription('');
  };

  // Fetch data from API
  const fetchData = async () => {
    try {
      setLoading(true);
      let url = `/admin/user/all-pre-insurance?PageNumber=${page + 1}&PageSize=${rowsPerPage}`;
      if (searchDropValue && searchQuery) {
        url += `&searchQuery=${encodeURIComponent(searchQuery)}&SearchColumn=${searchDropValue}`;
      } else if (searchQuery) {
        url += `&searchQuery=${encodeURIComponent(searchQuery)}`;
      }
      const response = await axios.get(url);
      if (response?.data?.statusCode === 200) {
        setData(response.data.response || []);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [page, rowsPerPage, searchQuery, searchDropValue]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilter = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const isNotFound = data.length === 0;

  return (
    <Page title="Assignment Registration">
      {loading && <Loader />}
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack direction="row" alignItems="center">
            <IconButton onClick={() => navigate(-1)}>
              <Iconify icon="eva:arrow-back-fill" width={24} height={24} />
            </IconButton>
            <Typography variant="h4" sx={{ ml: 1 }}>
              Assignment Registration
            </Typography>
          </Stack>
        </Stack>

        <Card>
          <ListToolbar
            searchQuery={searchQuery}
            onFilter={handleFilter}
            numSelected={0}
            hideDeleteButton={true}
            hideEmailButton={true}
            dropOptions={preInsuranceDropOptions}
            searchDropValue={searchDropValue}
            setSearchDropValue={setSearchDropValue}
          />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={data.length}
                  onRequestSort={handleRequestSort}
                  hideCheckboxColumnHeader={true}
                />
                <TableBody>
                  {data.map((row) => {
                    const { id, userId, user, customer, typeOfWork,coverageStart } = row;
                    const customerName = customer?.name || 'N/A';
                    const userName = ((user?.firstName || '') + ' ' + (user?.lastName || '')).trim() || 'N/A';
                    const userEmail = user?.email || 'N/A';
                    const customerIdValue = customer?.customerId || 'N/A';
                    const customerEmail = customer?.email || 'N/A';
                    const descriptionText = typeOfWork || '';
                    const wordCount = descriptionText.trim().split(/\s+/).filter(word => word.length > 0).length;
                    const shouldTruncate = wordCount > 10;

                    return (
                      <TableRow key={id} hover>
                        <TableCell>{userId || 'N/A'}</TableCell>
                        <TableCell>{userName || 'N/A'}</TableCell>
                        <TableCell>{userEmail || 'N/A'}</TableCell>
                        <TableCell>{customerIdValue}</TableCell>
                        <TableCell>{customerName}</TableCell>
                        <TableCell>{customerEmail}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{coverageStart ? formatDate(coverageStart) : 'N/A'}</TableCell>
                        <TableCell>
                          {shouldTruncate ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Tooltip title={descriptionText} placement="top">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {truncateDescription(descriptionText)}
                                </Typography>
                              </Tooltip>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenDescDialog(descriptionText)}
                                sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                              >
                                View
                              </Button>
                            </Stack>
                          ) : (
                            <Typography variant="body2">
                              {descriptionText || 'N/A'}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={searchQuery} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      {/* Description Dialog */}
      <Dialog
        open={openDescDialog}
        onClose={handleCloseDescDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Full Description</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
            {selectedDescription}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDescDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

